import React, { useState } from "react";
import { usePutV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberSendMutation } from "randmar-api-client";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useToast } from "randmarcomps";
import { useManufacturer } from "../../../contexts/WithManufacturer";
import { AlertTriangle } from "lucide-react";
import InlineSpinner from "../../util/InlineSpinner";

interface IProps {
  purchaseOrderNumber: string;
  children: React.ReactNode;
}

const SendPurchaseOrderToSupplier: React.FC<IProps> = ({ purchaseOrderNumber, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [sendPO, { isLoading }] = usePutV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberSendMutation();

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleConfirm = async () => {
    try {
      await sendPO({ routeManufacturerId: applicationId, purchaseOrderNumber }).unwrap();
      toast({ variant: "success", title: "Success", description: "PO successfully sent." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not send this PO." });
    }
    setOpen(false);
  };

  return (
    <>
      <div
        title={"Send " + purchaseOrderNumber + " to Manufacturer"}
        onClick={handleOpen}
        className="cursor-pointer"
      >
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isLoading && !isOpen && handleCancel()}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Send {purchaseOrderNumber} to the Manufacturer?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will send the purchase order to the supplier&apos;s PO email address. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? <InlineSpinner className="mr-2" decorative /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SendPurchaseOrderToSupplier;
